import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Download,
  X,
  EyeOff,
  Eye,
  ChevronDown,
  ChevronUp,
  FileText,
  FileX,
  ListX,
  IndianRupee,
  Ban,
  XCircle,
} from "lucide-react";
import { CSVLink } from "react-csv";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPrint, faEdit } from "@fortawesome/free-solid-svg-icons";
import { useCloud } from "../../hooks/useCloud";
import { Reprint } from "../../components/Print/ReprintBill";
import store from "../../store/index";
import PasswordDialog from "../PasswordInput";
import toast from "react-hot-toast";
import { SUCCESS } from "../../assets/constants";
import OrderBiller from "../../components/OrderBiller";
import PaymentWarning from "../../components/PaymentWarning";
import DateFilter from "../DateFilter";
import { observer } from "mobx-react-lite";
import dateStore from "../../hooks/useDate";
import StatsCard from "../stats/StatsCard";
import ReportLoadingState from "./LoadingState";
import { DataTable } from "../DataTable";
import PageHeader from "../PageHeader";
import BillDetailsModal from "../BillDetailedView"

const dateToString = (date) => {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getOrderStatus = (status) => {
  switch (status) {
    case "settle_later":
      return "Pending";
    case "pending":
      return "Live";
    case "paid":
      return "Paid";
    default:
      return status;
  }
};

const getOredrStatusColor = (status) => {
  switch (status) {
    case "settle_later":
      return "bg-yellow-100 text-yellow-800";
    case "pending":
      return "bg-[#FFA500]  text-white";
    case "cancelled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-green-100 text-green-800";
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const orderTypeMapping = {
  live: "pending",
  pending: "settle_later",
  all: "all",
};

const REPORT_TYPES = {
  ALL: "bill-wise-report",
  CANCELLED: "cancelled-bill-reports",
  CANCELLED_ITEMS: "cancelled-bill-item-reports",
  PARTNER_ORDER: "partner_order",
};

const REPORT_TABS = [
  {
    id: REPORT_TYPES.ALL,
    label: "All Orders",
    icon: FileText,
  },
  {
    id: REPORT_TYPES.PARTNER_ORDER,
    label: "Partner Orders",
    icon: FileText,
  },
  {
    id: REPORT_TYPES.CANCELLED,
    label: "Cancelled Orders",
    icon: FileX,
  },
  {
    id: REPORT_TYPES.CANCELLED_ITEMS,
    label: "Cancelled Items",
    icon: ListX,
  },
];

const VALID_REPORT_TYPES = [
  REPORT_TYPES.CANCELLED,
  REPORT_TYPES.CANCELLED_ITEMS,
  REPORT_TYPES.ALL,
];

const Orders = observer(() => {
  const { dateType, startDate, endDate } = dateStore;
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBillerOrder, setSelectedBillerOrder] = useState(null);
  const [isOrderBillerOpen, setIsOrderBillerOpen] = useState(false);
  const [showPasswordDialogue, setShowPasswordDialogue] = useState(false);
  const [isProtected, setProtected] = useState(true);
  const [abortController, setAbortController] = useState(null);

  // Add refs for controlling fetch behavior
  const fetchTimeoutRef = useRef(null);
  const isFirstRender = useRef(true);

  const [tablesVPAMapping] = useState(
    store.tables.reduce((acc, table) => {
      acc[table.qrpidVPA] = `${table.room} T${table.tableNo}`;
      return acc;
    }, {})
  );
  const [staffMapping] = useState(
    store.staff.reduce((acc, staff) => {
      acc[staff.id] = staff.name;
      return acc;
    }, {})
  );

  const {
    getOrderDetails,
    cancelledBillReport,
    cancelledBillItemReport,
    partnerOrderReport,
    reports,
  } = useCloud();

  const PARTNER_COLORS = {
    zomato: "#e11d48", // bright crimson pink-red
    swiggy: "#f97316", // vivid orange
  };

  const [loading, setLoading] = useState(true);
  const [isViewBillOpen, setIsViewBillOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [activeTab, setActiveTab] = useState(REPORT_TYPES.ALL);
  const [orderFilter, setOrderFilter] = useState("all"); // Add this line for filter state
  const [reportStats, setReportStats] = useState({
    [REPORT_TYPES.ALL]: {
      label: "Total Revenue",
      value: 0,
      count: 0,
      countLable: "Orders",
      chartData: [],
      showChart: true,
    },
    [REPORT_TYPES.CANCELLED]: {
      label: "Cancelled Amount",
      value: 0,
      count: 0,
      countLable: "Orders",
      chartData: [],
      showChart: true,
    },
    [REPORT_TYPES.CANCELLED_ITEMS]: {
      label: "Items Amount",
      value: 0,
      count: 0,
      countLable: "Dishes",
      chartData: [],
      showChart: false,
    },
    [REPORT_TYPES.PARTNER_ORDER]: {
      label: "Partner Orders",
      value: 0,
      count: null,
      countLable: "Revenue",
      chartData: [],
      showChart: false,
    },
  });
  const [ordersDataByTab, setOrdersDataByTab] = useState({
    [REPORT_TYPES.ALL]: [],
    [REPORT_TYPES.CANCELLED]: [],
    [REPORT_TYPES.CANCELLED_ITEMS]: [],
    [REPORT_TYPES.PARTNER_ORDER]: [],
  });
  const [paginationByTab, setPaginationByTab] = useState({
    [REPORT_TYPES.ALL]: {
      pageSize: 20,
      totalItems: 0,
      totalPages: 1,
      currentPage: 1,
    },
    [REPORT_TYPES.CANCELLED]: {
      pageSize: 20,
      totalItems: 0,
      totalPages: 1,
      currentPage: 1,
    },
    [REPORT_TYPES.CANCELLED_ITEMS]: {
      pageSize: 20,
      totalItems: 0,
      totalPages: 1,
      currentPage: 1,
    },
    [REPORT_TYPES.PARTNER_ORDER]: {
      pageSize: 20,
      totalItems: 0,
      totalPages: 1,
      currentPage: 1,
    },
  });

  const updatePage = (newPage) => {
    if (loading) return; // Prevent page updates while loading
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", newPage.toString());
    setSearchParams(newSearchParams);
    setPaginationByTab((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        currentPage: newPage,
      },
    }));

    if (activeTab === REPORT_TYPES.ALL) {
      fetchAllOrders({
        page: newPage,
      });
    } else if (activeTab === REPORT_TYPES.CANCELLED) {
      fetchCancelledOrders({
        page: newPage,
      });
    } else if (activeTab === REPORT_TYPES.CANCELLED_ITEMS) {
      fetchCancelledItems({
        page: newPage,
      });
    }
  };

  const fetchAllOrders = async ({
    startDate = dateStore.startDate,
    endDate = dateStore.endDate,
    dateType = dateStore.dateType,
    page = paginationByTab[REPORT_TYPES.ALL].currentPage,
    billStatus = orderFilter,
  }) => {
    try {
      const reports_data = await reports({
        dateType: dateType,
        page: page,
        customStart: startDate,
        customEnd: endDate,
        reportType: activeTab,
        signal: abortController?.signal,
        billStatus,
      });

      if (reports_data?.status === SUCCESS) {
        const {
          data: { orders, totalAmountInRange },
          pagination,
        } = reports_data;

        setOrdersDataByTab((prev) => ({
          ...prev,
          [REPORT_TYPES.ALL]: orders,
        }));

        setReportStats((prev) => ({
          ...prev,
          [REPORT_TYPES.ALL]: {
            ...prev[REPORT_TYPES.ALL],
            value: totalAmountInRange,
            count: pagination.totalItems,
          },
        }));

        const { pageSize, totalItems, totalPages } = pagination;
        setPaginationByTab((prev) => ({
          ...prev,
          [REPORT_TYPES.ALL]: {
            ...prev[REPORT_TYPES.ALL],
            totalPages: parseInt(totalPages),
            totalItems: parseInt(totalItems),
            pageSize: parseInt(pageSize),
            currentPage: parseInt(page),
          },
        }));
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
        toast.error("Something went wrong please try again");
      }
    }
  };

  const fetchPartnerOrderReport = async ({
    startDate = dateStore.startDate,
    endDate = dateStore.endDate,
    dateType = dateStore.dateType,
    page = paginationByTab[REPORT_TYPES.PARTNER_ORDER].currentPage,
  }) => {
    try {
      const res = await partnerOrderReport({
        dateType,
        startDate,
        endDate,
        page: page,
        signal: abortController?.signal,
      });

      const { status, data, pagination } = res;

      if (status === SUCCESS) {
        const {
          orders,
          partnerWiseRevenueReport,
          totalAmountInRange,
          totalAmountTillNow,
        } = data;
        setOrdersDataByTab((prev) => ({
          ...prev,
          [REPORT_TYPES.PARTNER_ORDER]: orders,
        }));

        //  [
        //     {
        //       "partnerName": "Zomato",
        //       "totalPaidOrders": 2,
        //       "totalRevenue": "270.00",
        //       "averageOrderValue": "135.00",
        //       "percentageOfTotal": "64.52"
        //   },
        //   {
        //       "partnerName": "Swiggy",
        //       "totalPaidOrders": 1,
        //       "totalRevenue": "148.50",
        //       "averageOrderValue": "148.50",
        //       "percentageOfTotal": "35.48"
        //   }
        // ]

        const chartData = partnerWiseRevenueReport.map((partner) => ({
          label: partner.partnerName,
          value: Number(partner.totalRevenue),
          color: PARTNER_COLORS[partner.partnerName.toLowerCase()],
        }));

        setReportStats((prev) => ({
          ...prev,
          [REPORT_TYPES.PARTNER_ORDER]: {
            ...prev[REPORT_TYPES.PARTNER_ORDER],
            value: totalAmountInRange,
            count: null,
            chartData,
            showChart: true,
          },
        }));

        setPaginationByTab((prev) => ({
          ...prev,
          [REPORT_TYPES.PARTNER_ORDER]: {
            ...prev[REPORT_TYPES.PARTNER_ORDER],
            totalPages: parseInt(pagination.totalPages),
            totalItems: parseInt(pagination.totalItems),
            pageSize: parseInt(pagination.pageSize),
            currentPage: parseInt(pagination.currentPage),
          },
        }));
      } else {
        toast.error("Something went wrong please try again");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
        toast.error("Something went wrong please try again");
      }
    }
  };

  const fetchCancelledOrders = async ({
    startDate = dateStore.startDate,
    endDate = dateStore.endDate,
    dateType = dateStore.dateType,
    page = paginationByTab[REPORT_TYPES.CANCELLED].currentPage,
  }) => {
    try {
      const res = await cancelledBillReport({
        dateType,
        startDate,
        endDate,
        page: page,
        signal: abortController?.signal,
      });

      const { status, data, pagination } = res.data;

      if (status === SUCCESS) {
        setOrdersDataByTab((prev) => ({
          ...prev,
          [REPORT_TYPES.CANCELLED]: data.cancelledBillDetails,
        }));

        setPaginationByTab((prev) => ({
          ...prev,
          [REPORT_TYPES.CANCELLED]: {
            ...prev[REPORT_TYPES.CANCELLED],
            totalPages: parseInt(pagination.totalPages),
            totalItems: parseInt(pagination.totalItems),
            pageSize: parseInt(pagination.pageSize),
            currentPage: parseInt(pagination.currentPage),
          },
        }));

        const overAllMetric = data.overviewDetails.reduce((acc, curr) => {
          acc[curr.metricName] = curr.amount;
          return acc;
        }, {});

        setReportStats((prev) => ({
          ...prev,
          [REPORT_TYPES.CANCELLED]: {
            ...prev[REPORT_TYPES.CANCELLED],
            count: overAllMetric.totalOrders,
            value: overAllMetric.totalAmount,
          },
        }));
      } else {
        toast.error("Something went wrong please try again");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
        toast.error("Something went wrong please try again");
      }
    }
  };

  const fetchCancelledItems = async ({
    startDate = dateStore.startDate,
    endDate = dateStore.endDate,
    dateType = dateStore.dateType,
    page = paginationByTab[REPORT_TYPES.CANCELLED_ITEMS].currentPage,
  }) => {
    try {
      const res = await cancelledBillItemReport({
        dateType,
        startDate,
        endDate,
        page: page,
        signal: abortController?.signal,
      });

      const { status, data, pagination } = res.data;

      if (status === SUCCESS) {
        setOrdersDataByTab((prev) => ({
          ...prev,
          [REPORT_TYPES.CANCELLED_ITEMS]: data.cancelledKotDetails,
        }));
        setPaginationByTab((prev) => ({
          ...prev,
          [REPORT_TYPES.CANCELLED_ITEMS]: {
            ...prev[REPORT_TYPES.CANCELLED_ITEMS],
            totalPages: parseInt(pagination.totalPages),
            totalItems: parseInt(pagination.totalItems),
            pageSize: parseInt(pagination.pageSize),
            currentPage: parseInt(pagination.currentPage),
          },
        }));

        const overAllMetric = data.overviewDetails.reduce((acc, curr) => {
          acc[curr.metricName] = curr.amount;
          return acc;
        }, {});
        setReportStats((prev) => ({
          ...prev,
          [REPORT_TYPES.CANCELLED_ITEMS]: {
            ...prev[REPORT_TYPES.CANCELLED_ITEMS],
            count: overAllMetric.totalOrders,
            value: overAllMetric.totalAmount,
          },
        }));
      } else {
        toast.error("Something went wrong please try again");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
        toast.error("Something went wrong please try again");
      }
    }
  };

  const fetchReports = async ({
    startDate = dateStore.startDate,
    endDate = dateStore.endDate,
    dateType = dateStore.dateType,
    page = 1,
  }) => {
    // Abort any existing requests
    if (abortController) {
      abortController.abort();
    }

    // Create new AbortController
    const controller = new AbortController();
    setAbortController(controller);

    try {
      setLoading(true);
      // Execute requests sequentially
      await fetchAllOrders({
        startDate,
        endDate,
        dateType,
        page,
        billStatus: orderTypeMapping[orderFilter],
      });
      if (controller.signal.aborted) return;

      await fetchCancelledOrders({ startDate, endDate, dateType, page });
      if (controller.signal.aborted) return;

      await fetchCancelledItems({ startDate, endDate, dateType, page });
      if (controller.signal.aborted) return;

      await fetchPartnerOrderReport({ startDate, endDate, dateType, page });
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch operation was aborted");
      } else {
        console.error("Error fetching reports:", error);
        toast.error("Something went wrong while fetching reports");
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    if (isFirstRender.current && startDate && endDate) {
      fetchReports({});
      isFirstRender.current = false;
    }
  }, []);

  // Handle subsequent data fetches with debounce
  // useEffect(() => {
  //   if (!isFirstRender.current && startDate && endDate && !loading) {
  //     // Clear any existing timeout
  //     if (fetchTimeoutRef.current) {
  //       clearTimeout(fetchTimeoutRef.current);
  //     }

  //     // Set new timeout
  //     fetchTimeoutRef.current = setTimeout(() => {
  //       fetchReports();
  //     }, 500);

  //     // Cleanup timeout on unmount or when dependencies change
  //     return () => {
  //       if (fetchTimeoutRef.current) {
  //         clearTimeout(fetchTimeoutRef.current);
  //       }
  //     };
  //   }
  // }, [startDate, endDate, dateType]);

  const handleBillPrint = async (order) => {
    try {
      const { data, status } = await getOrderDetails(order.orderId);
      if (status === SUCCESS) {
        const tableDetails = store.rooms.reduce(
          (acc, room) => {
            const table = room.tables.find(
              (table) => table.qrpidVPA === order.tableVPA
            );
            return table
              ? { roomName: room.roomName, tableNo: table.tableNo }
              : acc;
          },
          { roomName: "", tableNo: "" }
        );
        await Reprint({
          ...order,
          ...data,
          billStatus: getOrderStatus(order.billStatus),
          tableDetails,
        });
      } else {
        toast.error("Something went wrong please try again");
      }
    } catch (error) {
      toast.error("Something went wrong please try again");
    }
  };

  const handleBillView = async (order) => {
    try {
      const { data, status } = await getOrderDetails(order.orderId);
      if (status === SUCCESS) {
        setSelectedBill({ ...order, ...data });
        setIsViewBillOpen(true);
      } else {
        toast.error("Something went wrong please try again");
      }
    } catch (error) {
      toast.error("Something went wrong please try again");
    }
  };

  const handleBillUpdate = async (order) => {
    try {
      const { data, status } = await getOrderDetails(order.orderId);
      if (status === SUCCESS) {
        setSelectedBillerOrder({ ...order, ...data });
        setIsOrderBillerOpen(true);
      } else {
        toast.error("Something went wrong please try again");
      }
    } catch (error) {
      toast.error("Something went wrong please try again");
    }
  };

  const handleDateChange = ({
    startDate: newStart,
    endDate: newEnd,
    dateType: newType,
  }) => {
    if (loading) return; // Prevent changes while loading
    setPaginationByTab((prev) => ({
      ...prev,
      [REPORT_TYPES.ALL]: {
        ...prev[REPORT_TYPES.ALL],
        currentPage: 1,
      },
      [REPORT_TYPES.CANCELLED]: {
        ...prev[REPORT_TYPES.CANCELLED],
        currentPage: 1,
      },
      [REPORT_TYPES.CANCELLED_ITEMS]: {
        ...prev[REPORT_TYPES.CANCELLED_ITEMS],
        currentPage: 1,
      },
    }));

    fetchReports({
      startDate: newStart,
      endDate: newEnd,
      dateType: newType,
      page: 1,
    });
  };

  const unHideRevenue = (credentialResponse) => {
    if (credentialResponse.password === store.restaurantInfo.masterPassword) {
      setProtected(false);
      setShowPasswordDialogue(false);
    } else {
      toast.error("Wrong password");
    }
  };

  if (loading) {
    return (
      <ReportLoadingState
        dateType={dateType}
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
        onTypeChange={(type) => setDateType(type)}
        variant="orders"
        title="Orders Report"
        description="View and analyze order data, revenue, and payment information"
      />
    );
  }

  const getFilteredOrders = async (orderFilter) => {
    setOrderFilter((prev) => orderFilter);
    await fetchAllOrders({
      billStatus: orderTypeMapping[orderFilter],
    });
  };

  return (
    <>
      <div className="mb-4">
        <PageHeader
          title="Orders Report"
          description="View and analyze order data, revenue, and payment information"
          rightSlot={
            <DateFilter
              dateType={dateType}
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateChange}
              onTypeChange={(type) => setDateType(type)}
              reportType={
                VALID_REPORT_TYPES.includes(activeTab) ? activeTab : null
              }
            />
          }
          mobileDropdownContent={
            <DateFilter
              dateType={dateType}
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateChange}
              onTypeChange={(type) => setDateType(type)}
              reportType={
                VALID_REPORT_TYPES.includes(activeTab) ? activeTab : null
              }
            />
          }
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <StatsCard
          title="Total Revenue"
          value={
            isProtected
              ? "*****"
              : formatCurrency(reportStats[REPORT_TYPES.ALL].value)
          }
          subtitle={`${reportStats[REPORT_TYPES.ALL].count} ${
            reportStats[REPORT_TYPES.ALL].countLable
          }`}
          variant="success"
          showChart={reportStats[REPORT_TYPES.ALL].showChart}
          data={[
            {
              label: "Orders",
              value: reportStats[REPORT_TYPES.ALL].count,
              color: "#3B82F6",
            },
            {
              label: "Cancelled Orderss",
              value: reportStats[REPORT_TYPES.CANCELLED].count,
              color: "#D3D3D3",
            },
          ]}
        />

        <StatsCard
          title="Partner Orders"
          value={
            isProtected
              ? "*****"
              : formatCurrency(reportStats[REPORT_TYPES.PARTNER_ORDER].value)
          }
          variant="success"
          showChart={reportStats[REPORT_TYPES.PARTNER_ORDER].showChart}
          data={reportStats[REPORT_TYPES.PARTNER_ORDER].chartData}
        />

        <StatsCard
          title="Cancelled Orders"
          icon={Ban}
          value={
            isProtected
              ? "*****"
              : formatCurrency(reportStats[REPORT_TYPES.CANCELLED].value)
          }
          subtitle={`${reportStats[REPORT_TYPES.CANCELLED].count} ${
            reportStats[REPORT_TYPES.CANCELLED].countLable
          }`}
          variant="danger"
          showChart={reportStats[REPORT_TYPES.CANCELLED].showChart}
          data={[
            {
              label: "Orders",
              value: reportStats[REPORT_TYPES.ALL].count,
              color: "#D3D3D3",
            },
            {
              label: "Cancelled Orderss",
              value: reportStats[REPORT_TYPES.CANCELLED].count,
              color: "#F59E0B",
            },
          ]}
        />
        {/* <StatsCard
          title="Cancelled Items"
          icon={XCircle}
          value={
            isProtected
              ? "*****"
              : reportStats[REPORT_TYPES.CANCELLED_ITEMS].value.toLocaleString(
                  "en-IN"
                )
          }
          subtitle={`${reportStats[REPORT_TYPES.CANCELLED_ITEMS].count} ${
            reportStats[REPORT_TYPES.CANCELLED_ITEMS].countLable
          }`}
          variant="warning"
        /> */}
      </div>

      <div className="orders-container">
        <div className="orders-wrapper">
          {/* Header Section - Fixed */}
          <div className="orders-header-section">
            <div className="orders-header-content">
              {/* Revenue Info */}
              <div className="orders-revenue">
                {loading ? (
                  <div className="orders-loading-spinner" />
                ) : (
                  <div className="orders-revenue-info">
                    <div className="orders-revenue-title">
                      {reportStats[activeTab].label}
                      {isProtected ? (
                        <EyeOff
                          className="orders-revenue-icon"
                          onClick={() => setShowPasswordDialogue(true)}
                        />
                      ) : (
                        <Eye
                          className="orders-revenue-icon"
                          onClick={() => setProtected(true)}
                        />
                      )}
                    </div>
                    <div className="orders-revenue-amount">
                      {isProtected
                        ? "*****"
                        : reportStats[activeTab].value?.toLocaleString("en-IN")}
                    </div>
                    <div className="orders-revenue-count">
                      {reportStats[activeTab].count}{" "}
                      {reportStats[activeTab].countLable}
                    </div>
                  </div>
                )}
              </div>

              {/* Report Type Tabs */}
              <div className="orders-tabs gap-2">
                <div className="inline-flex items-center bg-gray-100 p-0.5 rounded-lg">
                  {REPORT_TABS.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                          activeTab === tab.id
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* filter button only if the all type report is selected */}
                {activeTab === REPORT_TYPES.ALL && (
                  <div className="orders-filter-button">
                    <select
                      className="inline-flex items-center bg-gray-100 px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-gray-200 border-none appearance-none pr-8 cursor-pointer"
                      value={orderFilter}
                      onChange={(e) => getFilteredOrders(e.target.value)}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: "right 0.5rem center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "1.5em 1.5em",
                      }}
                    >
                      <option value="all">All Orders</option>
                      <option value="live">Live Orders</option>
                      <option value="pending">Pending Orders</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="orders-table-section">
            {/* Table Section - Scrollable */}
            {activeTab === REPORT_TYPES.ALL && (
              <div className="orders-table-container hide-scrollbar">
                {loading ? (
                  <TableSkeleton rows={5} columns={10} hasHeader={true} />
                ) : (
                  <DataTable
                    data={ordersDataByTab[activeTab]}
                    columns={[
                      {
                        key: "billNo",
                        label: "Bill No",
                        width: "80px",
                        render: (row) => (
                          <div className="flex gap-1 items-center">
                            <span>{row.billNo}</span>
                          </div>
                        ),
                      },
                      {
                        key: "table",
                        label: "Table",
                        width: "200px",
                        render: (row) =>
                          row.typeOfOrder === "Takeaway"
                            ? "Parcel"
                            : `${tablesVPAMapping[row.tableVPA] || "-"}`,
                      },
                      {
                        key: "paymentmethod",
                        label: "Payment Type",
                        align: "center",
                        width: "120px",
                        render: (row) => (
                          <div className="text-center">
                            <p className="font-medium capitalize">
                              {row.paymentmethod}
                            </p>
                            {row.paymentmethod === "partial" &&
                              row?.partialAmoutsplit && (
                                <div>
                                  {row.partialAmoutsplit.map(
                                    (ele, idx) =>
                                      ele.amount > 0 && (
                                        <p
                                          key={idx}
                                          className="text-xs text-gray-600"
                                        >
                                          {ele.name}: {ele.amount}
                                        </p>
                                      )
                                  )}
                                </div>
                              )}
                          </div>
                        ),
                      },
                      {
                        key: "subTotal",
                        label: "My Amount",
                        align: "center",
                        width: "120px",
                        render: (row) => (
                          <div className="text-center">
                            {Number.parseFloat(row.billTxnLogs.subTotal)}
                          </div>
                        ),
                      },
                      {
                        key: "discount",
                        label: "Discount",
                        align: "center",
                        width: "120px",
                        render: (row) => (
                          <div className="text-center">
                            {Number.parseFloat(row.billTxnLogs.discount).toFixed(2)}
                          </div>
                        ),
                      },
                      {
                        key: "total",
                        label: "Grand Total",
                        align: "center",
                        width: "120px",
                        render: (row) => (
                          <div className="text-center">
                            {Number.parseFloat(row.billTxnLogs.total)}
                          </div>
                        ),
                      },
                      {
                        key: "tips",
                        label: "Tips",
                        align: "center",
                        width: "120px",
                        render: (row) => (
                          <div className="text-center">
                            {row.billTxnLogs.tip || 0}
                          </div>
                        ),
                      },
                      {
                        key: "status",
                        label: "Status",
                        align: "center",
                        width: "120px",
                        render: (row) => (
                          <div className="whitespace-nowrap flex justify-center">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getOredrStatusColor(
                                row.billStatus
                              )}`}
                              onClick={() => {
                                if (row.billStatus === "settle_later") {
                                  // handleOpenCancelModal(row); // This function is not defined in the original file
                                }
                              }}
                            >
                              {row.canceled
                                ? "Cancelled"
                                : getOrderStatus(row.billStatus)}
                            </span>
                            {row.orderType === "digital" && (
                              <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                Digital
                              </span>
                            )}
                          </div>
                        ),
                      },
                      {
                        key: "date",
                        label: "Date/Time",
                        align: "center",
                        width: "150px",
                        render: (row) => (
                          <div className="space-x-2 text-center">
                            <p className="text-xs">
                              {new Date(row.createdAt).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </p>
                            <p>{dateToString(row.createdAt)}</p>
                          </div>
                        ),
                      },
                      {
                        key: "actions",
                        width: "200px",
                        align: "end",
                        label: "Actions",
                        render: (row) => (
                          <div className="cursor-pointer text-gray-400 text-right">
                            <FontAwesomeIcon
                              icon={faEye}
                              className="mr-3"
                              onClick={() => handleBillView(row)}
                            />
                            {!row.canceled && (
                              <FontAwesomeIcon
                                icon={faPrint}
                                className="mr-3"
                                onClick={() => handleBillPrint(row)}
                              />
                            )}
                            {!row.canceled &&
                              ["settle_later", "pending"].includes(
                                row.billStatus
                              ) && (
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="mr-3"
                                  onClick={() => handleBillUpdate(row)}
                                />
                              )}
                          </div>
                        ),
                      },
                    ]}
                    sortableKeys={["billNo", "date", "total"]}
                    currentPage={paginationByTab[REPORT_TYPES.ALL].currentPage}
                    totalPages={paginationByTab[REPORT_TYPES.ALL].totalPages}
                    onPageChange={updatePage}
                    onPrev={() =>
                      updatePage(
                        Math.max(
                          paginationByTab[REPORT_TYPES.ALL].currentPage - 1,
                          1
                        )
                      )
                    }
                    onNext={() =>
                      updatePage(
                        Math.min(
                          paginationByTab[REPORT_TYPES.ALL].currentPage + 1,
                          paginationByTab[REPORT_TYPES.ALL].totalPages
                        )
                      )
                    }
                    onSort={(key, direction) => {
                      const sortedData = [
                        ...ordersDataByTab[REPORT_TYPES.ALL],
                      ].sort((a, b) => {
                        if (key === "date") {
                          return direction === "asc"
                            ? new Date(a.createdAt) - new Date(b.createdAt)
                            : new Date(b.createdAt) - new Date(a.createdAt);
                        }
                        if (key === "billNo") {
                          return direction === "asc"
                            ? a.billNo - b.billNo
                            : b.billNo - a.billNo;
                        }
                        if (key === "total") {
                          return direction === "asc"
                            ? a.billTxnLogs.total - b.billTxnLogs.total
                            : b.billTxnLogs.total - a.billTxnLogs.total;
                        }
                        return 0;
                      });
                      setOrdersDataByTab((prev) => ({
                        ...prev,
                        [REPORT_TYPES.ALL]: sortedData,
                      }));
                    }}
                    showHeader={false}
                    className="border-none"
                  />
                )}
              </div>
            )}

            {activeTab === REPORT_TYPES.CANCELLED && (
              <div className="orders-table-container hide-scrollbar">
                <DataTable
                  data={ordersDataByTab[activeTab]}
                  columns={[
                    {
                      key: "billNo",
                      label: "Bill No",
                      render: (row) => row.billNo,
                    },
                    {
                      key: "tableNo",
                      label: "Table",
                      width: "250px",
                      render: (row) =>
                        row.tableNo === "Takeaway"
                          ? "Parcel"
                          : tablesVPAMapping[row.tableNo] || "-",
                    },
                    {
                      key: "itemsOrdered",
                      label: "Items Ordered",
                      render: (row) => (
                        <div className="max-w-md">
                          <p className="truncate" title={row.itemsOrdered}>
                            {row.itemsOrdered}
                          </p>
                        </div>
                      ),
                    },
                    {
                      key: "totalAmount",
                      label: "Total Amount",
                      render: (row) =>
                        `₹${row.totalAmount.toLocaleString("en-IN")}`,
                    },
                    {
                      key: "cancellationReason",
                      label: "Cancellation Reason",
                      render: (row) => (
                        <div className="max-w-xs">
                          <p
                            className="truncate"
                            title={row.cancellationReason}
                          >
                            {row.cancellationReason}
                          </p>
                        </div>
                      ),
                    },
                    {
                      key: "time",
                      label: "Date/Time",
                      render: (row) => (
                        <div>
                          <span>
                            {new Date(row.time).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </span>
                          <span className="ml-2">
                            {dateToString(new Date(row.time))}
                          </span>
                        </div>
                      ),
                    },
                  ]}
                  sortableKeys={["billNo", "time", "totalAmount"]}
                  currentPage={paginationByTab[activeTab].currentPage}
                  totalPages={paginationByTab[activeTab].totalPages}
                  onPageChange={updatePage}
                  onPrev={() =>
                    updatePage(
                      Math.max(paginationByTab[activeTab].currentPage - 1, 1)
                    )
                  }
                  onNext={() =>
                    updatePage(
                      Math.min(
                        paginationByTab[activeTab].currentPage + 1,
                        paginationByTab[activeTab].totalPages
                      )
                    )
                  }
                  onSort={(key, direction) => {
                    const sortedData = [...ordersDataByTab[activeTab]].sort(
                      (a, b) => {
                        if (key === "time") {
                          return direction === "asc"
                            ? new Date(a.time) - new Date(b.time)
                            : new Date(b.time) - new Date(a.time);
                        }
                        if (key === "billNo") {
                          return direction === "asc"
                            ? a.billNo - b.billNo
                            : b.billNo - a.billNo;
                        }
                        if (key === "totalAmount") {
                          return direction === "asc"
                            ? a.totalAmount - b.totalAmount
                            : b.totalAmount - a.totalAmount;
                        }
                        return 0;
                      }
                    );
                    setOrdersDataByTab((prev) => ({
                      ...prev,
                      [activeTab]: sortedData,
                    }));
                  }}
                  showHeader={false}
                  className="border-none"
                />
              </div>
            )}

            {activeTab === REPORT_TYPES.CANCELLED_ITEMS && (
              <div className="orders-table-container hide-scrollbar">
                <DataTable
                  data={ordersDataByTab[activeTab]}
                  columns={[
                    {
                      key: "restaurantBillNo",
                      label: "Bill No",
                      render: (row) => row.restaurantBillNo,
                    },
                    {
                      key: "tableNo",
                      label: "Table",
                      width: "250px",
                      render: (row) =>
                        row.tableNo === "Takeaway"
                          ? "Parcel"
                          : tablesVPAMapping[row.tableNo] || "-",
                    },
                    {
                      key: "itemName",
                      label: "Item Name",
                      render: (row) => (
                        <div className="max-w-xs">
                          <p className="truncate" title={row.itemName}>
                            {row.itemName}
                          </p>
                        </div>
                      ),
                    },
                    {
                      key: "sellingPrice",
                      label: "Price",
                      render: (row) =>
                        `₹${parseFloat(row.sellingPrice).toLocaleString(
                          "en-IN"
                        )}`,
                    },
                    {
                      key: "quantity",
                      label: "Qty",
                      render: (row) => (
                        <div className="text-center">{row.quantity}</div>
                      ),
                    },
                    {
                      key: "totalAmount",
                      label: "Total",
                      render: (row) =>
                        `₹${row.totalAmount.toLocaleString("en-IN")}`,
                    },
                    {
                      key: "reason",
                      label: "Reason",
                      render: (row) => (
                        <div className="max-w-xs">
                          <p className="truncate" title={row.reason}>
                            {row.reason}
                          </p>
                        </div>
                      ),
                    },
                    {
                      key: "time",
                      align: "end",
                      width: "200px",
                      label: "Date/Time",
                      render: (row) => (
                        <div className="text-end">
                          <span>
                            {new Date(row.time).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </span>
                          <span className="ml-2">
                            {dateToString(new Date(row.time))}
                          </span>
                        </div>
                      ),
                    },
                  ]}
                  sortableKeys={[
                    "restaurantBillNo",
                    "time",
                    "totalAmount",
                    "quantity",
                  ]}
                  currentPage={paginationByTab[activeTab].currentPage}
                  totalPages={paginationByTab[activeTab].totalPages}
                  onPageChange={updatePage}
                  onPrev={() =>
                    updatePage(
                      Math.max(paginationByTab[activeTab].currentPage - 1, 1)
                    )
                  }
                  onNext={() =>
                    updatePage(
                      Math.min(
                        paginationByTab[activeTab].currentPage + 1,
                        paginationByTab[activeTab].totalPages
                      )
                    )
                  }
                  onSort={(key, direction) => {
                    const sortedData = [...ordersDataByTab[activeTab]].sort(
                      (a, b) => {
                        if (key === "time") {
                          return direction === "asc"
                            ? new Date(a.time) - new Date(b.time)
                            : new Date(b.time) - new Date(a.time);
                        }
                        if (key === "restaurantBillNo") {
                          return direction === "asc"
                            ? a.restaurantBillNo - b.restaurantBillNo
                            : b.restaurantBillNo - a.restaurantBillNo;
                        }
                        if (key === "totalAmount") {
                          return direction === "asc"
                            ? a.totalAmount - b.totalAmount
                            : b.totalAmount - a.totalAmount;
                        }
                        if (key === "quantity") {
                          return direction === "asc"
                            ? a.quantity - b.quantity
                            : b.quantity - a.quantity;
                        }
                        return 0;
                      }
                    );
                    setOrdersDataByTab((prev) => ({
                      ...prev,
                      [activeTab]: sortedData,
                    }));
                  }}
                  showHeader={false}
                  className="border-none"
                />
              </div>
            )}

            {activeTab === REPORT_TYPES.PARTNER_ORDER && (
              <div className="orders-table-container hide-scrollbar">
                <DataTable
                  data={ordersDataByTab[activeTab]}
                  columns={[
                    {
                      key: "billNo",
                      label: "Bill No",
                      render: (row) => (
                        <div className="flex gap-1 items-center">
                          <span>{row.billNo}</span>
                        </div>
                      ),
                    },
                    {
                      key: "partnerName",
                      label: "Partner Name",
                      render: (row) => row.partnerName,
                    },
                    {
                      key: "subTotal",
                      label: "My Amount",
                      render: (row) =>
                        Number.parseFloat(row.billTxnLogs.subTotal),
                    },
                    {
                      key: "discount",
                      label: "Discount",
                      render: (row) =>
                        Number.parseFloat(row.billTxnLogs.discount).toFixed(2),
                    },
                    {
                      key: "total",
                      label: "Grand Total",
                      render: (row) => Number.parseFloat(row.billTxnLogs.total),
                    },
                    {
                      key: "status",
                      label: "Status",
                      render: (row) => (
                        <div className="whitespace-nowrap flex">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getOredrStatusColor(
                              row.billStatus
                            )}`}
                            onClick={() => {
                              if (row.billStatus === "settle_later") {
                                // handleOpenCancelModal(row); // This function is not defined in the original file
                              }
                            }}
                          >
                            {row.canceled
                              ? "Cancelled"
                              : getOrderStatus(row.billStatus)}
                          </span>
                          {row.orderType === "digital" && (
                            <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              Digital
                            </span>
                          )}
                        </div>
                      ),
                    },
                    {
                      key: "date",
                      label: "Date/Time",
                      render: (row) => (
                        <div className="space-x-2">
                          <span>
                            {new Date(row.createdAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </span>
                          <span>{dateToString(row.createdAt)}</span>
                        </div>
                      ),
                    },
                    {
                      key: "actions",
                      label: "View/Print/Update",
                      render: (row) => (
                        <div className="cursor-pointer text-gray-400 text-right">
                          <FontAwesomeIcon
                            icon={faEye}
                            className="mr-3"
                            onClick={() => handleBillView(row)}
                          />
                          {!row.canceled && (
                            <FontAwesomeIcon
                              icon={faPrint}
                              className="mr-3"
                              onClick={() => handleBillPrint(row)}
                            />
                          )}
                          {!row.canceled &&
                            ["settle_later", "pending"].includes(
                              row.billStatus
                            ) && (
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="mr-3"
                                onClick={() => handleBillUpdate(row)}
                              />
                            )}
                        </div>
                      ),
                    },
                  ]}
                  sortableKeys={["billNo", "date", "total"]}
                  currentPage={
                    paginationByTab[REPORT_TYPES.PARTNER_ORDER].currentPage
                  }
                  totalPages={
                    paginationByTab[REPORT_TYPES.PARTNER_ORDER].totalPages
                  }
                  onPageChange={updatePage}
                  onPrev={() =>
                    updatePage(
                      Math.max(
                        paginationByTab[REPORT_TYPES.PARTNER_ORDER]
                          .currentPage - 1,
                        1
                      )
                    )
                  }
                  onNext={() =>
                    updatePage(
                      Math.min(
                        paginationByTab[REPORT_TYPES.PARTNER_ORDER]
                          .currentPage + 1,
                        paginationByTab[REPORT_TYPES.PARTNER_ORDER].totalPages
                      )
                    )
                  }
                  onSort={(key, direction) => {
                    const sortedData = [
                      ...ordersDataByTab[REPORT_TYPES.PARTNER_ORDER],
                    ].sort((a, b) => {
                      if (key === "date") {
                        return direction === "asc"
                          ? new Date(a.createdAt) - new Date(b.createdAt)
                          : new Date(b.createdAt) - new Date(a.createdAt);
                      }
                      if (key === "billNo") {
                        return direction === "asc"
                          ? a.billNo - b.billNo
                          : b.billNo - a.billNo;
                      }
                      if (key === "total") {
                        return direction === "asc"
                          ? a.billTxnLogs.total - b.billTxnLogs.total
                          : b.billTxnLogs.total - a.billTxnLogs.total;
                      }
                      return 0;
                    });
                    setOrdersDataByTab((prev) => ({
                      ...prev,
                      [REPORT_TYPES.PARTNER_ORDER]: sortedData,
                    }));
                  }}
                  showHeader={false}
                  className="border-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <PasswordDialog
          isOpen={showPasswordDialogue}
          onClose={() => setShowPasswordDialogue(false)}
          onSubmit={unHideRevenue}
        />
        <PaymentWarning message="Please verify your payment details before proceeding." />

        {isViewBillOpen && selectedBill && (
          <BillDetailsModal
            selectedBill={selectedBill}
            setIsViewBillOpen={setIsViewBillOpen}
          />
        )}

        {isOrderBillerOpen && selectedBillerOrder && (
          <OrderBiller
            isOpen={isOrderBillerOpen}
            onClose={() => setIsOrderBillerOpen(false)}
            onUpdate={async (orderId) => {
              const { data, status } = await getOrderDetails(orderId);
              if (status === SUCCESS) {
                setOrdersDataByTab((prev) => ({
                  ...prev,
                  [REPORT_TYPES.ALL]: prev[REPORT_TYPES.ALL].map((o) =>
                    o.orderId === data.orderId ? data : o
                  ),
                }));
                setIsOrderBillerOpen(false);
                setSelectedBillerOrder(null);
              } else {
                toast.error("Something went wrong please try again");
              }
            }}
            order={selectedBillerOrder}
          />
        )}
      </div>
    </>
  );
});

// const BillDetailsModal = ({ selectedBill, setIsViewBillOpen }) => {
//   const [isAnimating, setIsAnimating] = useState(true);

//   useEffect(() => {
//     const handleEscape = (e) => {
//       if (e.key === "Escape") {
//         handleClose();
//       }
//     };
//     document.addEventListener("keydown", handleEscape);
//     return () => document.removeEventListener("keydown", handleEscape);
//   }, [setIsViewBillOpen]);

//   useEffect(() => {
//     const timer = setTimeout(() => {
//       setIsAnimating(false);
//     }, 50);
//     return () => clearTimeout(timer);
//   }, []);

//   const handleClose = () => {
//     setIsAnimating(true);
//     setTimeout(() => {
//       setIsViewBillOpen(false);
//     }, 300);
//   };

//   const handleReceiptPrint = async () => {
//     const order = selectedBill;
//     const tableDetails = store.rooms.reduce(
//       (acc, room) => {
//         const table = room.tables.find(
//           (table) => table.qrpidVPA === order.tableVPA
//         );
//         return table
//           ? { roomName: room.roomName, tableNo: table.tableNo }
//           : acc;
//       },
//       { roomName: "", tableNo: "" }
//     );
//     await Reprint({
//       ...order,
//       billStatus: getOrderStatus(order.billStatus),
//       tableDetails,
//     });
//   };

//   return (
//     <div className="fixed inset-0 z-50">
//       {/* Backdrop */}
//       <div
//         className={`fixed inset-0 bg-black transition-opacity duration-300 ${
//           isAnimating ? "bg-opacity-0" : "bg-opacity-50"
//         }`}
//         onClick={handleClose}
//       />

//       {/* Drawer */}
//       <div
//         className={`fixed inset-y-0 right-0 w-full sm:w-[40%] bg-white shadow-xl transform transition-transform duration-300 ease-out ${
//           isAnimating ? "translate-x-full" : "translate-x-0"
//         }`}
//       >
//         {/* Header */}
//         <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-gray-300">
//           <div className="flex items-center justify-between">
//             <div>
//               <h1 className="text-xl font-bold">
//                 Bill No #{selectedBill.billNo}
//               </h1>
//               <p className="text-gray-500 text-sm">
//                 {new Date(selectedBill.createdAt).toLocaleString()}
//               </p>
//             </div>
//             <button
//               className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
//               onClick={handleClose}
//             >
//               <span className="sr-only">Close panel</span>
//               <X className="h-6 w-6" />
//             </button>
//           </div>
//         </div>

//         {/* Content */}
//         <div className="overflow-y-auto h-[calc(100vh-140px)] px-6 py-4">
//           {/* Status & Payment Info */}
//           <div className="flex justify-between items-center mb-4 text-sm">
//             <span
//               className={`px-3 py-1 rounded-full font-medium ${getOredrStatusColor(
//                 selectedBill.billStatus
//               )}`}
//             >
//               {getOrderStatus(selectedBill.billStatus)}
//             </span>
//             <span className="text-gray-600 capitalize">
//               {selectedBill?.paymentmethod}
//             </span>
//           </div>

//           {selectedBill.canceled && (
//             <div className="mb-4 bg-red-50 border border-red-100 p-2 rounded text-sm">
//               <span className="text-red-800 font-medium">CANCELLED</span>
//               <span className="text-red-600 ml-2">
//                 Reason: {selectedBill?.cancelReason || "Not specified"}
//               </span>
//             </div>
//           )}

//           {/* Order Items */}
//           <div className="mb-4">
//             <div className="bg-gray-50 rounded overflow-hidden">
//               <div className="grid grid-cols-12 gap-2 bg-gray-100 p-2 text-xs font-medium text-gray-700">
//                 <div className="col-span-4">Item</div>
//                 <div className="col-span-2">Variant</div>
//                 <div className="col-span-2 text-center">Qty</div>
//                 <div className="col-span-2 text-right">Price</div>
//                 <div className="col-span-2 text-right">Final Amount</div>
//               </div>

//               <div className="divide-y divide-gray-100">
//                 {selectedBill?.items?.map((item, index) => (
//                   <div
//                     key={index}
//                     className="grid grid-cols-12 gap-2 p-2 items-center text-sm"
//                   >
//                     <div className="col-span-4">
//                       <p className="font-medium text-gray-800">
//                         {item?.name || "Unknown Item"}
//                       </p>
//                       {item.isCancelled && (
//                         <span className="text-xs text-red-600">Cancelled</span>
//                       )}
//                     </div>
//                     <div className="col-span-2 text-gray-600 text-sm">
//                       {item?.variantName !== "-" ? item.variantName : "—"}
//                     </div>
//                     <div className="col-span-2 text-center">
//                       {item?.quantity || "1"}
//                     </div>
//                     <div className="col-span-2 text-right font-medium">
//                       ₹{Number.parseFloat(item?.price).toFixed(2)}
//                     </div>
//                     <div className="col-span-2 text-right font-medium">
//                       ₹
//                       {(
//                         Number.parseFloat(item?.price) * (item?.quantity || 1)
//                       ).toFixed(2)}
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>

//           {/* Transaction Details */}
//           <div className="text-sm">
//             <div className="grid grid-cols-2 gap-x-4 gap-y-2">
//               {/* Subtotal Section */}
//               <div className="col-span-2 font-medium border-b pb-1 mb-1">
//                 Sub Total
//               </div>
//               <div className="text-gray-600">Sub Total Amount</div>
//               <div className="text-right">
//                 ₹{selectedBill?.billTxnLogs?.subTotal || 0}
//               </div>

//               {/* Discounts Section */}
//               {selectedBill?.billTxnLogs?.discount > 0 && (
//                 <>
//                   <div className="col-span-2 font-medium border-b pb-1 mb-1 mt-3">
//                     Discounts
//                   </div>
//                   <div className="text-gray-600">Discount Amount</div>
//                   <div className="text-right text-green-600">
//                     -₹{selectedBill?.billTxnLogs?.discount}
//                   </div>

//                   <div className="text-gray-600">Flat Discount</div>
//                   <div className="text-right">
//                     ₹{selectedBill?.billTxnLogs?.discountFlat || 0}
//                   </div>

//                   <div className="text-gray-600">Discount Percentage</div>
//                   <div className="text-right">
//                     {selectedBill?.billTxnLogs?.discountPercent || 0}%
//                   </div>
//                 </>
//               )}

//               {/* Taxes Section */}
//               {selectedBill?.billTxnLogs?.gst && (
//                 <>
//                   <div className="col-span-2 font-medium border-b pb-1 mb-1 mt-3">
//                     Taxes
//                   </div>
//                   <div className="text-gray-600">GST Number</div>
//                   <div className="text-right">
//                     {selectedBill?.billTxnLogs?.gstNo || "N/A"}
//                   </div>

//                   <div className="text-gray-600">GST Type</div>
//                   <div className="text-right">
//                     {selectedBill?.billTxnLogs?.gstExcluded
//                       ? "Excluded"
//                       : "Included"}
//                   </div>

//                   <div className="text-gray-600">
//                     CGST ({selectedBill?.billTxnLogs?.cgstPercent || 0}%)
//                   </div>
//                   <div className="text-right">
//                     ₹{selectedBill?.billTxnLogs?.cgst || 0}
//                   </div>

//                   <div className="text-gray-600">
//                     SGST ({selectedBill?.billTxnLogs?.sgstPercent || 0}%)
//                   </div>
//                   <div className="text-right">
//                     ₹{selectedBill?.billTxnLogs?.sgst || 0}
//                   </div>

//                   <div className="col-span-2 font-medium border-b pb-1 mb-1 mt-2">
//                     Additional Charges
//                   </div>

//                   <div className="text-gray-600">Service Charge</div>
//                   <div className="text-right">
//                     {selectedBill?.billTxnLogs?.service ? "Yes" : "No"}
//                   </div>
//                 </>
//               )}

//               <div className="col-span-2 mt-2">
//                 <div className="flex justify-between font-medium text-base bg-gray-50 p-2 rounded">
//                   <span>Net Amount</span>
//                   <span>
//                     ₹
//                     {(
//                       selectedBill?.billTxnLogs?.beforeDiscountTotal || 0
//                     ).toFixed(2)}
//                   </span>
//                 </div>
//               </div>

//               {/* Tips Section */}
//               {selectedBill?.billTxnLogs?.tip > 0 && (
//                 <div className="col-span-2 mt-2">
//                   <div className="flex justify-between text-sm text-gray-600">
//                     <span>Tips</span>
//                     <span>₹{selectedBill?.billTxnLogs?.tip}</span>
//                   </div>
//                 </div>
//               )}

//               {selectedBill?.billTxnLogs?.total !==
//                 selectedBill?.billTxnLogs?.beforeDiscountTotal +
//                   selectedBill?.billTxnLogs?.tip && (
//                 <div className="col-span-2 mt-2">
//                   <div className="flex justify-between text-sm text-gray-600">
//                     <span>Round off</span>
//                     <span>
//                       ₹
//                       {(
//                         selectedBill?.billTxnLogs?.total -
//                         (selectedBill?.billTxnLogs?.beforeDiscountTotal +
//                           selectedBill?.billTxnLogs?.tip)
//                       ).toFixed(2)}
//                     </span>
//                   </div>
//                 </div>
//               )}

//               {/* Total Section */}
//               <div className="col-span-2 pt-2 mt-3">
//                 <div className="flex justify-between font-bold text-base bg-gray-50 p-2 rounded">
//                   <span>Total Amount</span>
//                   <span>₹{selectedBill?.billTxnLogs?.total}</span>
//                 </div>
//                 <div className="flex justify-between text-sm text-gray-500 mt-2">
//                   <span>Payment Type</span>
//                   <span className="capitalize">
//                     {selectedBill?.billTxnLogs?.paymentType || "N/A"}
//                   </span>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="sticky bottom-0 z-10 bg-white flex gap-2 p-4 border-t">
//           <button
//             className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded transition-colors"
//             onClick={handleClose}
//           >
//             Close
//           </button>
//           <button
//             className="flex-1 bg-gray-800 hover:bg-gray-900 text-white py-2 px-4 rounded transition-colors"
//             onClick={() => handleReceiptPrint()}
//           >
//             Print
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

export default Orders;
